/**
 * Enveloppe applicative de `@exiplan/gantt` : chargement des tâches/dépendances,
 * sélecteur de zoom, bascule chemin critique, et câblage des callbacks vers l'API
 * (mise à jour optimiste, rollback sur échec, message de cycle en 409).
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gantt } from '@exiplan/gantt';
import { api, ApiError } from '../lib/api.js';
import { useAllTasks } from '../hooks/useAllTasks.js';
import { appAlert } from '../dialogs/service.js';
import { Empty, LoadError, TruncatedTasksWarning } from '../components/common.jsx';
import { BTN_GHOST_SM } from '../ui/theme.js';

/** Rassemble les dépendances portées par chaque tâche, dédupliquées par id. */
function collectDependencies(tasks) {
  const byId = new Map();
  for (const task of tasks) {
    const groups = [task.dependencies, task.outgoing, task.incoming].filter(Array.isArray);
    for (const group of groups) {
      for (const dep of group) byId.set(dep.id, dep);
    }
  }
  return [...byId.values()];
}

export function GanttView({ projectId, projectKey, onOpenTask, reloadKey }) {
  const { t, i18n } = useTranslation();
  const [zoom, setZoom] = useState('day');
  const [showCriticalPath, setShowCriticalPath] = useState(false);
  const [collapsedIds, setCollapsedIds] = useState([]);

  // Toutes les pages sont chargées (§ issue B5) : un chemin critique calculé sur un sous-
  // ensemble de tâches serait faux sans que rien ne le dise.
  const {
    tasks: tasksPage,
    truncated,
    error,
    reload,
    patch,
  } = useAllTasks(projectId, { deps: [reloadKey] });

  // La référence lisible n'existe pas côté API (qui ne renvoie que `number`) : on la
  // compose ici, sinon la colonne de gauche du Gantt n'affiche que des titres.
  const tasks = useMemo(
    () =>
      (tasksPage ?? []).map((task) => ({
        ...task,
        reference: `${projectKey ?? '…'}-${task.number}`,
      })),
    [tasksPage, projectKey],
  );
  const dependencies = useMemo(() => collectDependencies(tasks), [tasks]);

  const onToggleCollapse = useCallback((taskId) => {
    setCollapsedIds((current) =>
      current.includes(taskId) ? current.filter((id) => id !== taskId) : [...current, taskId],
    );
  }, []);

  const onTaskChange = useCallback(
    async ({ id, startDate, dueDate }) => {
      const previous = tasks;
      patch((current) =>
        (current ?? []).map((t) => (t.id === id ? { ...t, startDate, dueDate } : t)),
      );
      try {
        await api.patch(`/projects/${projectId}/tasks/${id}`, { startDate, dueDate });
      } catch (error) {
        patch(() => previous);
        await appAlert(t('gantt.moveRejected', { message: error.message }));
      }
    },
    [projectId, patch, tasks, t],
  );

  const onDependencyCreate = useCallback(
    async ({ predecessorId, successorId }) => {
      try {
        await api.post(`/projects/${projectId}/tasks/${successorId}/dependencies`, {
          predecessorId,
          successorId,
          type: 'FS',
        });
      } catch (error) {
        const message =
          error instanceof ApiError && error.status === 409
            ? t('gantt.dependencyRejected', { message: error.message })
            : t('gantt.dependencyImpossible', { message: error.message });
        await appAlert(message);
      }
    },
    [projectId, t],
  );

  if (error) return <LoadError error={error} onRetry={reload} />;
  if (!tasksPage) return null;
  if (tasks.length === 0) {
    return <Empty>{t('gantt.empty')}</Empty>;
  }

  return (
    <div className="flex flex-col gap-2">
      {truncated && <TruncatedTasksWarning />}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={BTN_GHOST_SM}
          aria-pressed={showCriticalPath}
          onClick={() => setShowCriticalPath((v) => !v)}
        >
          {t('gantt.criticalPath')}
        </button>
        {/* Légende (palette pastel, 2026-09-28) : le chemin critique se lit par sa FORME. En
            thème clair, cadre de barre, sélection et chemin critique partagent l'encre. */}
        {showCriticalPath && (
          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-label text-ink-soft">
            <svg
              width="16"
              height="2"
              viewBox="0 0 16 2"
              aria-hidden="true"
              className="text-accent-ink"
            >
              <line
                x1="0"
                y1="1"
                x2="16"
                y2="1"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="4 2"
              />
            </svg>
            {t('gantt.criticalPathLegend')}
          </span>
        )}
      </div>
      <Gantt
        tasks={tasks}
        dependencies={dependencies}
        zoom={zoom}
        onZoomChange={setZoom}
        selectedId={null}
        onSelect={onOpenTask}
        onTaskChange={onTaskChange}
        onDependencyCreate={onDependencyCreate}
        showCriticalPath={showCriticalPath}
        collapsedIds={collapsedIds}
        onToggleCollapse={onToggleCollapse}
        locale={i18n.resolvedLanguage || i18n.language?.split('-')[0]}
      />
    </div>
  );
}
